import React, { useContext, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaEnvelope, FaClock, FaRocket, FaQuestionCircle, FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../Contexts/UserContext';
import { Helmet } from 'react-helmet';

function SignUpSuccess() {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const styles = {
        container: {
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            padding: '3rem 1rem',
        },
        card: {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: '16px',
            padding: '2.5rem',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden',
        },
        successIcon: {
            fontSize: '4rem',
            color: '#48BB78',
            marginBottom: '1.5rem',
        },
        title: {
            color: '#2D3748',
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            textAlign: 'center',
        },
        subtitle: {
            color: '#4A5568',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            marginBottom: '2rem',
            textAlign: 'center',
        },
        image: {
            width: '60%',
            maxWidth: '300px',
            height: 'auto',
            margin: '2rem auto',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            display: 'block',
        },
        sectionTitle: {
            color: '#2D3748',
            fontSize: '1.5rem',
            fontWeight: '600',
            marginTop: '2rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: '1.5rem 0',
        },
        listItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem',
            marginBottom: '1.5rem',
            color: '#4A5568',
            fontSize: '1.1rem',
            lineHeight: '1.5',
        },
        icon: {
            color: '#4299E1',
            fontSize: '1.5rem',
            marginTop: '0.2rem',
        },
        support: {
            background: '#EBF8FF',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
        },
        supportText: {
            color: '#2B6CB0',
            margin: 0,
            fontSize: '1rem',
        },
        link: {
            color: '#4299E1',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#2B6CB0',
            },
        },
        button: {
            background: 'linear-gradient(135deg, #4299E1 0%, #2B6CB0 100%)',
            border: 'none',
            padding: '0.8rem 2rem',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '1.1rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '2rem auto 0',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(66, 153, 225, 0.3)',
            },
        },
        decorativeCircle: {
            position: 'absolute',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(66, 153, 225, 0.1) 0%, rgba(43, 108, 176, 0.1) 100%)',
            zIndex: 0,
        },
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 },
    };

    useEffect(() => {
        // Check if user is populated and has a token
        if (user?.token) {
            // Perform the redirect or other action if token is missing
            navigate('/user_profile');
        }
    }, [navigate, user?.token]);

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Sign Up Success</title>
                <meta name="description" content="Congratulations! Your account has been successfully created on DNACloudHub. Get started with running qpAdm models and exploring advanced genetic analysis tools on our cloud platform." />
                <meta property="og:title" content={'DNACloudHub | Sign Up Success'} />
                <meta property="og:description" content={'"Congratulations! Your account has been successfully created on DNACloudHub. Get started with running qpAdm models and exploring advanced genetic analysis tools on our cloud platform."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/signup_success`} />
            </Helmet>

            <Container style={styles.container}>
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={containerVariants}
                            style={styles.card}
                        >
                            {/* Decorative circles */}
                            <div style={{ ...styles.decorativeCircle, width: '300px', height: '300px', top: '-150px', right: '-150px' }} />
                            <div style={{ ...styles.decorativeCircle, width: '200px', height: '200px', bottom: '-100px', left: '-100px' }} />

                            <motion.div variants={itemVariants} style={{ textAlign: 'center' }}>
                                <FaCheckCircle style={styles.successIcon} />
                                <h1 style={styles.title}>Success! Your Account is Being Reviewed</h1>
                                <p style={styles.subtitle}>
                                    Thank you for signing up on <strong>DNACloudHub</strong>! We're excited to have you on board.
                                </p>
                            </motion.div>

                            <motion.img
                                variants={itemVariants}
                                src='./signupsucess1.jpeg'
                                style={styles.image}
                                alt='signup success'
                            />

                            <motion.div variants={itemVariants}>
                                <h2 style={styles.sectionTitle}>
                                    <FaRocket style={styles.icon} /> What's Next?
                                </h2>
                                <ul style={styles.list}>
                                    <motion.li variants={itemVariants} style={styles.listItem}>
                                        <FaEnvelope style={styles.icon} />
                                        <div>
                                            <strong>Check Your Email:</strong> Please check your inbox for an email with details on how to log in once your account is approved.
                                        </div>
                                    </motion.li>
                                    <motion.li variants={itemVariants} style={styles.listItem}>
                                        <FaClock style={styles.icon} />
                                        <div>
                                            <strong>Wait for Approval:</strong> Our team will review your details, and your account will be activated soon.
                                        </div>
                                    </motion.li>
                                    <motion.li variants={itemVariants} style={styles.listItem}>
                                        <FaRocket style={styles.icon} />
                                        <div>
                                            <strong>Get Started:</strong> Once approved, you'll have full access to all the features of DNACloudHub, including the ability to run your custom qpAdm models in the cloud.
                                        </div>
                                    </motion.li>
                                </ul>
                            </motion.div>

                            <motion.div variants={itemVariants} style={styles.support}>
                                <FaQuestionCircle style={{ ...styles.icon, color: '#2B6CB0' }} />
                                <p style={styles.supportText}>
                                    If you have any questions in the meantime, feel free to{' '}
                                    <a href="mailto:admin@dnacloudhub.com" style={styles.link}>contact support</a>.
                                    We're here to help!
                                </p>
                            </motion.div>

                            <motion.div variants={itemVariants} style={{ textAlign: 'center' }}>
                                <Button
                                    href="/"
                                    style={styles.button}
                                    className="d-inline-flex align-items-center"
                                >
                                    <FaHome /> Back to Home
                                </Button>
                            </motion.div>
                        </motion.div>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default SignUpSuccess;
